import { supabase } from "@/lib/supabase-client";
import { IncidentType } from "@/types/db/incidents";

async function enrichIncidents(rawIncidents: any[]) {
  // Extract unique IDs from meta_data
  const userIds = new Set<string>();
  const projectIds = new Set<string>();
  const incidentTypeIds = new Set<string>();
  rawIncidents?.forEach((incident: any) => {
    const submittedBy = incident.meta_data?.submitted_by;
    if (submittedBy) {
      userIds.add(submittedBy);
    }
    const projectId = incident.meta_data?.project_id;
    if (projectId) {
      projectIds.add(projectId);
    }
    const incidentTypeId = incident.meta_data?.incident_type_id;
    if (incidentTypeId) {
      incidentTypeIds.add(incidentTypeId);
    }
  });

  // Fetch user data for submitted_by users
  let usersMap = new Map<string, any>();
  if (userIds.size > 0) {
    const { data: users } = await supabase
      .from("users")
      .select("id, first_name, last_name, preferred_name")
      .in("id", Array.from(userIds));

    if (users) {
      users.forEach((user) => {
        usersMap.set(user.id, user);
      });
    }
  }

  // Fetch project data
  let projectsMap = new Map<string, any>();
  if (projectIds.size > 0) {
    const { data: projects } = await supabase
      .from("projects")
      .select("id, name, status, meta_data, client_id, client:clients(id, name)")
      .in("id", Array.from(projectIds));

    if (projects) {
      projects.forEach((project) => {
        projectsMap.set(project.id, project);
      });
    }
  }

  // Fetch incident type data
  let incidentTypesMap = new Map<string, any>();
  if (incidentTypeIds.size > 0) {
    const { data: incidentTypes } = await supabase
      .schema("lists")
      .from("incident_types")
      .select("id, name, description")
      .in("id", Array.from(incidentTypeIds));

    if (incidentTypes) {
      incidentTypes.forEach((type) => {
        incidentTypesMap.set(type.id, type);
      });
    }
  }

  // Enrich incidents with user, project, incident_type, occurred_date, and location data
  return rawIncidents?.map((incident: any) => {
    const submittedById = incident.meta_data?.submitted_by;
    const submittedByUser = submittedById ? usersMap.get(submittedById) : null;
    const projectId = incident.meta_data?.project_id;
    const project = projectId ? projectsMap.get(projectId) : null;
    const incidentTypeId = incident.meta_data?.incident_type_id;
    const incidentType = incidentTypeId ? incidentTypesMap.get(incidentTypeId) : null;
    const occurredDate = incident.meta_data?.occurred_date || null;
    const location = incident.meta_data?.location || null;

    return {
      // Project fields first for default column ordering
      project: project ? { name: project.name, id: project.id } : null,
      project_status: project?.status || null,
      project_billing_status: (project?.meta_data as any)?.billing_status || null,
      ...incident,
      submitted_by_user: submittedByUser
        ? {
            name: `${submittedByUser.preferred_name || submittedByUser.first_name} ${submittedByUser.last_name}`,
            id: submittedByUser.id,
          }
        : null,
      incident_type: incidentType?.name || null,
      occurred_date: occurredDate,
      location: location,
    };
  });
}

export const incidentService = {
  async getIncidents() {
    const { data: rawIncidents, error } = await supabase
      .from("incidents")
      .select("id, name, description, status, created_at, meta_data")
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    return enrichIncidents(rawIncidents || []);
  },

  async getIncidentsByClientId(clientId: string) {
    const { data: rawIncidents, error } = await supabase
      .from("incidents")
      .select("id, name, description, status, created_at, meta_data")
      .eq("meta_data->>client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    return enrichIncidents(rawIncidents || []);
  },

  async getIncidentsByProjectId(projectId: string) {
    const { data: rawIncidents, error } = await supabase
      .from("incidents")
      .select("id, name, description, status, created_at, meta_data")
      .eq("meta_data->>project_id", projectId)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    return enrichIncidents(rawIncidents || []);
  },

  async getIncidentsByShiftId(shiftId: string) {
    const { data: rawIncidents, error } = await supabase
      .from("incidents")
      .select("id, name, description, status, created_at, meta_data")
      .eq("meta_data->>shift_id", shiftId)
      .order("created_at", { ascending: false });

    if (error) {
      throw Error(error.message);
    }

    return enrichIncidents(rawIncidents || []);
  },

  async getIncidentById(id: string): Promise<IncidentType> {
    const { data, error } = await supabase.from("incidents").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }
    return data as IncidentType;
  },

  async addIncident(incident: Partial<IncidentType> & { tenant_id: string }): Promise<IncidentType> {
    const { data, error } = await supabase.from("incidents").insert([incident]).select().single();

    if (error) {
      throw Error(error.message);
    }
    return data as IncidentType;
  },

  async updateIncident(id: string, patch: Partial<IncidentType>): Promise<IncidentType> {
    const { data, error } = await supabase.from("incidents").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as IncidentType;
  },

  async deleteIncident(id: string): Promise<boolean> {
    const { error } = await supabase.from("incidents").delete().eq("id", id);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async deleteIncidentsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.from("incidents").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async saveRowPatches(patches: Partial<IncidentType> & { id: string }[]): Promise<void> {
    await Promise.all(
      patches.map(async (patch) => {
        const { error } = await supabase.from("incidents").update(patch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async getUnresolvedIncidentsCount(): Promise<number> {
    const { data, error } = await supabase.from("incidents").select("id").neq("status", "RESOLVED");

    if (error) {
      console.error("Failed to fetch unresolved incidents count:", error);
      return 0;
    }
    return data?.length ?? 0;
  },

  async getIncidentsByShiftAndUser(shiftId: string, userId: string): Promise<IncidentType[]> {
    const { data, error } = await supabase
      .from("incidents")
      .select("*")
      .eq("meta_data->>shift_id", shiftId)
      .eq("meta_data->>submitted_by", userId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch incidents for shift: ${error.message}`);
    }

    return (data || []) as IncidentType[];
  },
};
